// /dashboard: referrals, earnings and the balance, the referral link, Stripe payouts and withdrawing. Live: the
// realtime hub says when the numbers moved, and they are read again.

import type { PageContext } from '../router.ts';
import { cabinetApi } from '../cabinet-api.ts';
import { config } from '../config.ts';
import { h, svg } from '../dom.ts';
import { formatCurrency } from '../format.ts';
import { t } from '../i18n.ts';
import { log } from '../logs.ts';
import { track } from '../metrics.ts';
import { connectHub } from '../realtime.ts';
import { snackbar } from '../snackbar.ts';

const K = 'influencer.web.dashboard';

const stripeLogo = () => svg(`<svg fill="none" height="20" viewBox="0 0 48 20" width="48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path clip-rule="evenodd" d="M47.9974 10.331C47.9974 6.91404 46.3442 4.21783 43.1844 4.21783C40.0112 4.21783 38.0913 6.91404 38.0913 10.3043C38.0913 14.3219 40.3578 16.3508 43.611 16.3508C45.1976 16.3508 46.3975 15.9904 47.3041 15.4832V12.8137C46.3975 13.2675 45.3576 13.5478 44.0376 13.5478C42.7444 13.5478 41.5978 13.094 41.4511 11.519H47.9708C47.9708 11.3454 47.9974 10.6514 47.9974 10.331ZM41.4111 9.063C41.4111 7.55473 42.3311 6.92739 43.171 6.92739C43.9843 6.92739 44.8509 7.55473 44.8509 9.063H41.4111Z" fill="#635BFF" fill-rule="evenodd"/><path clip-rule="evenodd" d="M32.9448 4.21783C31.6382 4.21783 30.7982 4.83182 30.3316 5.25895L30.1583 4.4314H27.2251V19.9947L30.5582 19.2872L30.5716 15.5099C31.0515 15.8569 31.7582 16.3508 32.9314 16.3508C35.318 16.3508 37.4912 14.4287 37.4912 10.1975C37.4779 6.32675 35.278 4.21783 32.9448 4.21783ZM32.1448 13.4143C31.3582 13.4143 30.8916 13.134 30.5716 12.787L30.5582 7.83503C30.9049 7.44795 31.3849 7.181 32.1448 7.181C33.3581 7.181 34.198 8.54245 34.198 10.291C34.198 12.0796 33.3714 13.4143 32.1448 13.4143Z" fill="#635BFF" fill-rule="evenodd"/><path clip-rule="evenodd" d="M22.6387 3.43033L25.9852 2.70956V0L22.6387 0.707421V3.43033Z" fill="#635BFF" fill-rule="evenodd"/><path d="M25.9852 4.44476H22.6387V16.1239H25.9852V4.44476Z" fill="#635BFF"/><path clip-rule="evenodd" d="M19.0524 5.43245L18.8391 4.44473H15.9592V16.1239H19.2924V8.20875C20.079 7.18098 21.4123 7.36785 21.8256 7.51467V4.44473C21.3989 4.28456 19.839 3.99091 19.0524 5.43245Z" fill="#635BFF" fill-rule="evenodd"/><path clip-rule="evenodd" d="M12.3859 1.54834L9.13272 2.24241L9.11938 12.9338C9.11938 14.9093 10.5993 16.3641 12.5725 16.3641C13.6658 16.3641 14.4658 16.1639 14.9057 15.9237V13.2141C14.4791 13.3876 12.3725 14.0016 12.3725 12.0262V7.2878H14.9057V4.44476H12.3725L12.3859 1.54834Z" fill="#635BFF" fill-rule="evenodd"/><path clip-rule="evenodd" d="M3.37315 7.83503C3.37315 7.31447 3.79979 7.11426 4.50642 7.11426C5.51969 7.11426 6.79962 7.42125 7.8129 7.9685V4.83182C6.70629 4.39135 5.61302 4.21783 4.50642 4.21783C1.7999 4.21783 0 5.63268 0 7.9952C0 11.6791 5.06638 11.0918 5.06638 12.6802C5.06638 13.2942 4.53308 13.4944 3.78646 13.4944C2.67985 13.4944 1.2666 13.0406 0.146658 12.4266V15.6033C1.38659 16.1372 2.63985 16.3641 3.78646 16.3641C6.55964 16.3641 8.4662 14.9893 8.4662 12.6001C8.45286 8.62253 3.37315 9.32995 3.37315 7.83503Z" fill="#635BFF" fill-rule="evenodd"/></svg>`);

const externalIcon = () => svg(`<svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8.00008 4H4.00008C3.64646 4 3.30732 4.14048 3.05727 4.39052C2.80722 4.64057 2.66675 4.97971 2.66675 5.33333V12C2.66675 12.3536 2.80722 12.6928 3.05727 12.9428C3.30732 13.1929 3.64646 13.3333 4.00008 13.3333H10.6667C11.0204 13.3333 11.3595 13.1929 11.6096 12.9428C11.8596 12.6928 12.0001 12.3536 12.0001 12V8" stroke="#175EE1" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25"/><path d="M7.33325 8.66669L13.3333 2.66669" stroke="#175EE1" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25"/><path d="M10 2.66669H13.3333V6.00002" stroke="#175EE1" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25"/></svg>`);

/** @param {import("../router.js").PageContext} ctx */
export function dashboard(ctx: PageContext) {
    interface Details {
        referralsCount: number;
        totalEarned: number;
        currentBalance: number;
        currency: { id?: string; code?: string } | null;
        accountId: string | null;
        detailsSubmitted: boolean;
        referralUrl: string;
    }

    const s: {
        details: Details;
        expanded: boolean;
        stripeUrlLoading: boolean;
        withdrawAmount: string;
        withdrawLoading: boolean;
    } = {
        details: { referralsCount: 0, totalEarned: 0, currentBalance: 0, currency: null, accountId: null, detailsSubmitted: false, referralUrl: '' },
        expanded: false,
        stripeUrlLoading: false,
        withdrawAmount: '0',
        withdrawLoading: false
    };

    ctx.title(`${K}.title`, 'Dashboard');
    track('influencerDashboardViewed');

    async function loadDetails() {
        const response = await cabinetApi('control/details');

        if (!ctx.active) return;

        if (response.ok) {
            const details = response.json<Partial<Details>>();

            if (details && typeof details === 'object') s.details = { ...s.details, ...details };
        }

        ctx.rerender();
    }

    async function copyLink() {
        track('influencerReferralCopied');

        try {
            await navigator.clipboard.writeText(s.details.referralUrl || '');
            snackbar(t(`${K}.notifications.linkCopy.success`), 'success');
        } catch {
            snackbar(t(`${K}.notifications.linkCopy.fail`), 'error');
        }
    }

    async function withdraw() {
        const amount = Number(String(s.withdrawAmount).replace(',', '.'));

        track('influencerWithdrawalRequested', { amount: Number.isFinite(amount) ? amount : 0 });

        if (!(amount > 0)) {
            snackbar(t(`${K}.notifications.invalidSum`), 'error');

            return;
        }

        s.withdrawLoading = true;
        ctx.rerender();

        try {
            const response = await cabinetApi('control/withdraw', {
                method: 'POST',
                json: { Amount: amount, CurrencyId: s.details.currency?.id ?? '00000000-0000-0000-0000-000000000000' }
            });

            if (response.ok && response.json<{ successful?: boolean }>()?.successful === true) {
                snackbar(t(`${K}.notifications.withdrawSuccess`), 'success');
            } else {
                snackbar(t(`${K}.notifications.withdrawError`), 'error');
            }

            await loadDetails();
        } catch (error) {
            snackbar(t(`${K}.notifications.withdrawError`), 'error');
            log.error('Error during withdrawal', error);
        } finally {
            s.withdrawLoading = false;
            ctx.rerender();
        }
    }

    // Two addresses, because Stripe tells a finished onboarding from an expired link only by where it lands: back to
    // this page, or to the handoff page that explains what happened.
    async function linkStripe() {
        track('influencerStripeLinkStarted', { state: s.details.detailsSubmitted ? 'connected' : 'not_connected' });

        s.stripeUrlLoading = true;
        ctx.rerender();

        const returnUrl = location.href;
        const refreshUrl = `${location.origin}/stripe/refresh`;
        const response = await cabinetApi(
            `control/payout-connection-link?redirectUrl=${encodeURIComponent(returnUrl)}&refreshUrl=${encodeURIComponent(refreshUrl)}`);

        s.stripeUrlLoading = false;
        ctx.rerender();

        const stripeUrl = response.ok ? response.string().trim() : '';

        if (stripeUrl) {
            window.open(stripeUrl);
        } else {
            snackbar(t(`${K}.notifications.stripeUrlLoadError`), 'error');
        }
    }

    const copyButton = () => h('button', { type: 'button', class: 'btn copy-link-button', onclick: () => ctx.run(copyLink) },
        h('img', { src: '/images/copy-link-button-icon.svg', alt: '' }),
        t(`${K}.copyReferralLinkButton.text`));

    function payoutBlock() {
        const d = s.details;

        if (d.accountId === null || d.accountId === undefined) {
            return h('div', { class: 'block' },
                h('div', { class: 'block-title' }, t(`${K}.linkAccount.title`)),
                h('button', { type: 'button', class: 'btn link-stripe-button', disabled: s.stripeUrlLoading, onclick: () => ctx.run(linkStripe) },
                    stripeLogo(), t(`${K}.linkAccountButton.text`)));
        }

        if (!d.detailsSubmitted) {
            return h('div', { class: 'block' },
                h('div', { class: 'block-title' }, t(`${K}.completeAccountLinking.title`)),
                h('div', { class: 'block-subtitle' }, t(`${K}.completeAccountLinking.subtitle`)),
                h('button', { type: 'button', class: 'btn link-stripe-button', disabled: s.stripeUrlLoading, onclick: () => ctx.run(linkStripe) },
                    stripeLogo(), t(`${K}.completeAccountLinkingButton.text`)));
        }

        return h('div', { class: 'block withdraw-block' },
            h('div', { class: 'block-title' }, t(`${K}.withdraw.title`)),
            h('input', {
                class: 'withdraw-input',
                type: 'number',
                inputmode: 'decimal',
                min: '0',
                step: '0.01',
                value: s.withdrawAmount,
                'aria-label': t(`${K}.withdraw.title`),
                oninput: (event: Event) => {
                    s.withdrawAmount = (event.target as HTMLInputElement).value;
                }
            }),
            h('button', { type: 'button', class: 'btn withdraw-button', disabled: s.withdrawLoading, onclick: () => ctx.run(withdraw) },
                t(`${K}.withdrawButton.text`)),
            h('div', { class: 'block-footer' },
                h('img', { src: '/images/stripe_logo.svg', alt: 'Stripe' }),
                h('button', { type: 'button', class: 'btn manage-account', disabled: s.stripeUrlLoading, onclick: () => ctx.run(linkStripe) },
                    t(`${K}.manageAccountButton.text`), externalIcon())));
    }

    function view() {
        const d = s.details;
        const code = d.currency?.code;

        return h('div', { class: 'page-dashboard' },
            h('div', { class: 'page-content' },
                h('div', { class: 'title' }, t(`${K}.title`)),
                h('div', { class: 'block' },
                    h('div', { class: 'block-items' },
                        h('div', { class: 'block-item' },
                            h('div', { class: 'block-item-title' }, t(`${K}.referrals.title`)),
                            h('div', { class: 'block-item-value' }, String(d.referralsCount ?? 0))),
                        h('div', { class: 'bar' }),
                        h('div', { class: 'block-item' },
                            h('div', { class: 'block-item-title' }, t(`${K}.totalEarned.title`)),
                            h('div', { class: 'block-item-value' }, formatCurrency(d.totalEarned, code))),
                        h('div', { class: 'bar' }),
                        d.detailsSubmitted
                            ? h('div', { class: 'block-item' },
                                h('div', { class: 'block-item-title' }, t(`${K}.availableToWithdraw.title`)),
                                h('div', { class: 'block-item-value' }, formatCurrency(d.currentBalance, code)))
                            : h('div', { class: 'block-item inactive' },
                                h('div', { class: 'block-item-title' }, t(`${K}.pendingWithdrawalSetup.title`)),
                                h('div', { class: 'block-item-value' }, formatCurrency(d.currentBalance, code))))),
                h('div', { class: 'blocks-row' },
                    h('div', { class: 'block referral-link' },
                        h('div', { class: 'share-block-title' }, t(`${K}.shareReferralLink.title`)),
                        h('div', { class: 'share-block-subtitle' }, t(`${K}.shareReferralLink.subtitle`)),
                        h('div', { class: 'link-row' },
                            h('div', { class: 'link' }, d.referralUrl || ''),
                            copyButton())),
                    payoutBlock()),

                // On a phone the referral link lives in a sheet at the bottom of the screen.
                h('div', { class: 'bottom-collapsable-overlay', hidden: !s.expanded, onclick: (event: MouseEvent) => setExpanded(event, false) }),
                h('div', { class: 'bottom-collapsable' },
                    h('div', {
                        class: `title ${s.expanded ? 'expanded' : ''}`,
                        role: 'button',
                        tabindex: 0,
                        'aria-expanded': s.expanded ? 'true' : 'false',
                        onclick: (event: MouseEvent) => setExpanded(event, !s.expanded),
                        onkeydown: (event: KeyboardEvent) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return;

                            event.preventDefault();
                            setExpanded(event, !s.expanded);
                        }
                    }, t(`${K}.shareReferralLink.title`), h('img', { src: '/images/expand_collapse_icon.svg', alt: '' })),
                    h('div', { class: 'collapse' + (s.expanded ? ' open' : '') },
                        h('div', null,
                            h('div', { class: 'collapsable-content' },
                                h('div', { class: 'subtitle' }, t(`${K}.shareReferralLink.subtitle`)),
                                h('div', { class: 'link' }, d.referralUrl || ''),
                                copyButton()))))));
    }

    // The sheet opens and closes in place rather than by a redraw, so it slides as MudCollapse did.
    function setExpanded(event: Event, expanded: boolean): void {
        const page = (event.currentTarget as HTMLElement).closest('.page-dashboard');

        s.expanded = expanded;

        if (!page) return;

        const title = page.querySelector('.bottom-collapsable .title');

        title?.classList.toggle('expanded', expanded);
        title?.setAttribute('aria-expanded', String(expanded));
        page.querySelector('.bottom-collapsable .collapse')?.classList.toggle('open', expanded);

        const overlay = page.querySelector<HTMLElement>('.bottom-collapsable-overlay');

        if (overlay) overlay.hidden = !expanded;
    }

    ctx.render(view);

    ctx.run(async () => {
        await loadDetails();

        if (!ctx.active) return;

        // After the first paint, and nothing else waits on it: a socket that cannot open costs this page only the
        // live updates. The server sends a reason, not figures - /control/details is read again.
        const hub = connectHub(`${config.influencerApiUrl.replace(/\/$/, '')}/hubs/realtime`, {
            handlers: { DashboardChanged: () => ctx.run(loadDetails) },
            onReconnected: () => ctx.run(loadDetails)
        });

        ctx.onDestroy(() => hub.stop());
    });
}
